import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import {
  checkDiscoveredAlive,
  deployAgent,
  discoveredCredentialsQuery,
  discoveredTabsQuery,
  precheckAgent,
  removeAgent,
  removeDiscovered,
  type DiscoveredCredential,
  type DiscoveredSent,
  type DiscoveredTabKey,
} from '../api/discoveredActions'
import { toast } from '../ui/notify'

/**
 * Zona care se deschide in dreapta cand se bifeaza randuri in Live Devices.
 *
 * In portal e `MultiActionsSidebar` din `public/js/general.js`, deschisa de
 * `MultipleRowsSelect` prin `multiRowsAllChecked` / `multiRowsAllUnchecked`
 * (`public/js/views/inventoree/live/index-1.js`), cu continutul incarcat de
 * `DiscoveryMultiActions` din `discovery/multiActionsTabs` si cu fiecare fila
 * alt cap de controller. Aici e acelasi markup (`.multiactions-sidebar`,
 * `.page-quick-sidebar-*`, `.multiple-actions-nav`, `#multiple-actions-content`),
 * deci si aceleasi reguli din `public/css/app.css`, dar filele cer DATE si isi
 * deseneaza singure formularul.
 *
 * Regula de aratat/ascuns e cea din portal: panoul apare cand e bifat cel putin
 * un rand si dispare cand se scoate ultima bifa. Butonul de inchis il inchide
 * fara sa scoata bifele - urmatoarea schimbare de bifa il deschide la loc,
 * exact ca `MultiActionsSidebar.closeSidebar()`.
 */

function apiToast(err: unknown) {
  const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Something went wrong'
  toast(message, 'Error', 'error')
}

/** Cate au plecat chiar, cand nu toate au un releu prin care sa fie strigate. */
function sentNote(saved: DiscoveredSent): string {
  const noRelay = saved.noRelay ?? 0
  if (noRelay < 1) return 'Instruction sent.'
  const sent = saved.sent ?? 0
  return `Instruction sent for ${sent} of ${saved.devices}; ${noRelay} have no relay to reach them.`
}

function Collapse({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="widget-collapse dark">
      <header>
        <a>
          <i className="collapse-caret fa fa-angle-up"></i> {title}
        </a>
      </header>
      <section className="collapse in" style={{ height: 'auto' }}>
        <div className="collapse-body" style={{ padding: 40 }}>
          {children}
        </div>
      </section>
    </div>
  )
}

/** Numele din lista, ca in `_credential_picker.php`: intrarea SI contul. */
function credentialLabel(c: DiscoveredCredential): string {
  const name = c.description.trim() || c.device_name.trim() || `#${c.id}`
  const user = c.user.trim()
  return user === '' ? name : `${name} - ${user}`
}

/**
 * `discovery/multiple_agent_deploy` si `discovery/multiple_agent_remove` - doua
 * masti cu acelasi formular, care se despart doar prin capatul lor.
 *
 * Ce cont foloseste releul ca sa puna sau sa scoata agentul: un rand din Pass
 * Safe, ca in `discovery/_credential_picker.php`. PAROLA NU PLEACA DE AICI si
 * nu intra in coada: comanda poarta doar numarul intrarii, iar contul il pune
 * API-ul in clipa in care da comanda agentului.
 *
 * Pana la reparatia asta formularul cerea Domain / User / Password in clar si
 * API-ul le scria in `it_comenzi.Comanda`, la vedere, cat traia randul.
 */
function CredentialsTab({
  ids,
  title,
  button,
  busyLabel,
  intro,
  run,
  done,
}: {
  ids: number[]
  title: string
  button: string
  busyLabel: string
  /** Textul de deasupra formularului (fila de proba il are, celelalte nu). */
  intro?: string
  run(ids: number[], credentialId: number): Promise<DiscoveredSent>
  done(): void
}) {
  const vault = useQuery(discoveredCredentialsQuery)
  const [credentialId, setCredentialId] = useState(0)
  const [busy, setBusy] = useState(false)
  const credentials = vault.data?.credentials ?? []

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (busy || credentialId < 1) return
    setBusy(true)
    run(ids, credentialId)
      .then((saved) => {
        setCredentialId(0)
        toast(sentNote(saved), title, (saved.noRelay ?? 0) > 0 ? 'warning' : 'success')
        done()
      })
      .catch(apiToast)
      .finally(() => setBusy(false))
  }

  return (
    <Collapse title={title}>
      <form className="form-horizontal" onSubmit={submit}>
        {intro !== undefined && (
          <div className="form-group">
            <div className="col-md-offset-3 col-md-9">
              <p className="text-muted">{intro}</p>
            </div>
          </div>
        )}
        <div className="form-group">
          <label className="col-md-3 control-label" htmlFor="discovered-credential">
            Credential
          </label>
          <div className="col-md-9">
            {vault.isPending ? (
              <p className="form-control-static">Loading Pass Safe...</p>
            ) : credentials.length === 0 ? (
              <p className="form-control-static">
                <i className="fas fa-info-circle"></i> This company has no Pass Safe entry yet, and the agent cannot be
                installed without an account. <a href="/app/settings/pass_safe/index">Add one in Pass Safe</a>, then come back.
              </p>
            ) : (
              <>
                <select
                  id="discovered-credential"
                  className="form-control"
                  name="credential_id"
                  value={credentialId === 0 ? '' : String(credentialId)}
                  onChange={(e) => setCredentialId(Number(e.target.value) || 0)}
                  required
                >
                  <option value="">Select a credential...</option>
                  {credentials.map((c) => (
                    <option key={c.id} value={c.id}>
                      {credentialLabel(c)}
                    </option>
                  ))}
                </select>
                <span className="help-block">
                  <i className="fas fa-info-circle"></i> The password never leaves Pass Safe: the queued command carries
                  only the entry, and the account is filled in when the agent picks it up.
                </span>
              </>
            )}
          </div>
        </div>
        <div className="form-group offset">
          <div className="col-md-offset-3 col-md-9">
            <button type="submit" className="btn btn-primary" disabled={busy || credentials.length === 0 || credentialId < 1}>
              {busy ? busyLabel : button}
            </button>
            <span className="help-block">{ids.length} selected.</span>
          </div>
        </div>
      </form>
    </Collapse>
  )
}

/** `discovery/multiple_actions`: Check if alive si Remove devices. */
function ActionsTab({ ids, done }: { ids: number[]; done(): void }) {
  const [busy, setBusy] = useState('')

  const check = () => {
    if (busy || !window.confirm('Are you sure?')) return
    setBusy('check')
    checkDiscoveredAlive(ids)
      .then((saved) => {
        toast(sentNote(saved), 'Discovered', (saved.noRelay ?? 0) > 0 ? 'warning' : 'success')
        done()
      })
      .catch(apiToast)
      .finally(() => setBusy(''))
  }

  /* Textul intrebarii e cel din `DiscoveredRemoval.ask`: se sterge din LISTA de
     gasite, nu din inventar, iar urmatoarea scanare le gaseste din nou. */
  const remove = () => {
    if (busy) return
    const question = ids.length === 1 ? 'Delete this device?' : `Delete ${ids.length} devices?`
    if (
      !window.confirm(
        `${question}\n\nIt is removed from the discovered list, not from the inventory.`
        + ' If it is still on the network, the next scan finds it again.',
      )
    ) {
      return
    }
    setBusy('remove')
    removeDiscovered(ids)
      .then((saved) => {
        toast(saved.devices === 1 ? 'Device deleted.' : `${saved.devices} devices deleted.`, 'Discovered')
        done()
      })
      .catch(apiToast)
      .finally(() => setBusy(''))
  }

  return (
    <Collapse title="DEVICE ACTIONS">
      <div className="form-group">
        <button className="btn btn-white" type="button" onClick={check} disabled={busy !== ''}>
          <i className="fas fa-search" aria-hidden="true"></i> Check if alive
        </button>
      </div>
      <div className="form-group">
        <button className="btn btn-danger" type="button" onClick={remove} disabled={busy !== ''}>
          <i className="fas fa-times" aria-hidden="true"></i> Remove devices
        </button>
      </div>
    </Collapse>
  )
}

export function DiscoveredMultiActions({ ids, onDone }: { ids: number[]; onDone(): void }) {
  const key = ids.join(',')
  const tabs = useQuery(discoveredTabsQuery)
  const [dismissed, setDismissed] = useState(false)
  const [active, setActive] = useState<DiscoveredTabKey | null>(null)

  /* Portalul redeschide panoul la orice schimbare de bifa, si asa se pierde si
     inchiderea de mana - vezi `multiRowsChange` -> `openSidebar`. */
  useEffect(() => {
    setDismissed(false)
  }, [key])

  const drawn = tabs.data?.tabs ?? []
  const current = active !== null && drawn.some((t) => t.key === active) ? active : (drawn[0]?.key ?? null)
  const open = ids.length > 0 && !dismissed && drawn.length > 0

  return (
    <div className={`multiactions-sidebar${open ? ' page-quick-sidebar-open' : ''}`}>
      <div className="page-quick-sidebar-wrapper quick-sidebar-navigation" style={{ width: '50%' }}>
        <div className="page-quick-sidebar-actions">
          <a
            className="close-sidebar"
            role="button"
            tabIndex={0}
            onClick={() => setDismissed(true)}
            onKeyDown={(e) => e.key === 'Enter' && setDismissed(true)}
          >
            <i className="fas fa-indent"></i>
          </a>
        </div>
        <div className="page-quick-sidebar avatar-slide">
          {open && (
            <>
              <div className="multiple-actions-nav">
                {drawn.map((tab) => (
                  <a
                    key={tab.key}
                    className={`btn btn-multi-tabs${current === tab.key ? ' active' : ''}`}
                    href="#"
                    title={tab.label}
                    onClick={(e) => {
                      e.preventDefault()
                      setActive(tab.key)
                    }}
                  >
                    <i className={tab.icon}></i> {tab.label}
                  </a>
                ))}
              </div>
              <div id="multiple-actions-content">
                {current === 'precheck' && (
                  <CredentialsTab
                    key={key}
                    ids={ids}
                    title="Check before deploy"
                    button="Run check"
                    busyLabel="Sending..."
                    intro={
                      'Answers "would the install work?" for each selected machine, without installing anything: '
                      + 'address, the port the install would use, and one sign-in with this credential. It leaves a '
                      + "sign-in entry in each machine's log, and nothing else."
                    }
                    run={precheckAgent}
                    done={onDone}
                  />
                )}
                {current === 'deploy' && (
                  <CredentialsTab
                    key={key}
                    ids={ids}
                    title="Deploy Agent"
                    button="Run Deploy"
                    busyLabel="Sending..."
                    run={deployAgent}
                    done={onDone}
                  />
                )}
                {current === 'remove-agent' && (
                  <CredentialsTab
                    key={key}
                    ids={ids}
                    title="Remove Agent"
                    button="Remove agent"
                    busyLabel="Sending..."
                    run={removeAgent}
                    done={onDone}
                  />
                )}
                {current === 'actions' && <ActionsTab key={key} ids={ids} done={onDone} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
